/**
 * Shared responsive-layout signals (brief 11-layout2 §1) — the single source
 * of truth both the drawer chrome (App.vue) and the Tessellation panel's
 * internal layout (§2) key off, so "docked vs inline" is never two
 * separately-maintained checks.
 *
 * - `isLandscape` — aspect ratio, not device category: width > height docks
 *   the tool strip and Tessellation/Layers tab access to opposite edges;
 *   height > width puts the tool strip in a row under the top bar and keeps
 *   the tab access as a bottom bar. A landscape phone behaves like a small
 *   tablet automatically; a portrait tablet behaves like a big phone.
 * - `isInlinePanel` — "full-width inline" vs "docked/narrow" panels. Narrow
 *   is the default everywhere (desktop, landscape tablet, phone in either
 *   orientation); inline is the exception, gated on portrait AND a width a
 *   phone won't reach — so a phone held in portrait still gets the narrow
 *   treatment, matching desktop, not "mobile vs desktop" as a device check.
 *
 * Kept in a composable (not computed inline from `window` in each component)
 * so every consumer reads the exact same breakpoint via matchMedia's live
 * list rather than a one-off resize listener.
 */
import { onBeforeUnmount, onMounted, ref, type Ref } from 'vue';

/** Must match the `@media (orientation: portrait) and (min-width: ...)`
 *  rules in App.vue / PatternPanel.vue — there is no CSS-variable-in-media-
 *  query mechanism in plain CSS, so this is the single JS-side source of
 *  truth; the CSS copies mention this constant in a comment. */
export const INLINE_PANEL_QUERY = '(orientation: portrait) and (min-width: 768px)';
/** width > height, independent of device category. */
export const LANDSCAPE_QUERY = '(min-aspect-ratio: 1/1)';

function useMediaQuery(query: string): Ref<boolean> {
  const matches = ref(false);
  let mql: MediaQueryList | null = null;
  const sync = (): void => {
    matches.value = mql?.matches ?? false;
  };
  onMounted(() => {
    mql = window.matchMedia(query);
    sync();
    mql.addEventListener('change', sync);
  });
  onBeforeUnmount(() => mql?.removeEventListener('change', sync));
  return matches;
}

export function useLayoutMode(): { isLandscape: Ref<boolean>; isInlinePanel: Ref<boolean> } {
  return {
    isLandscape: useMediaQuery(LANDSCAPE_QUERY),
    isInlinePanel: useMediaQuery(INLINE_PANEL_QUERY),
  };
}
